import React from 'react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, ReferenceLine,
} from 'recharts';
import { DailyMetrics } from '../types';
import BaselineDeviation from './BaselineDeviation';
import HeatmapCalendar from './HeatmapCalendar';
import { formatDate } from '../lib/dashboardHelpers';

interface Props {
  showTrends: boolean;
  onToggle: () => void;
  range: number;
  recentMetrics: DailyMetrics[];
  metrics: DailyMetrics[];
}

export default React.memo(function DashboardTrends({ showTrends, onToggle, range, recentMetrics, metrics }: Props) {
  const chartData = recentMetrics.map(d => ({
    date: formatDate(d.date),
    sleep: d.sleep_score,
    readiness: d.readiness,
    hrv: d.hrv,
  }));

  return (
    <div>
      <button
        onClick={onToggle}
        className="flex items-center justify-between w-full py-2.5 px-4 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors group"
      >
        <span className="section-label group-hover:text-arete-navy transition-colors">
          Recent Trends
        </span>
        <span
          className="text-arete-muted group-hover:text-arete-navy transition-all duration-200"
          style={{ display: 'inline-block', transform: showTrends ? 'rotate(180deg)' : 'rotate(0deg)' }}
        >
          ▾
        </span>
      </button>
      {showTrends && (
        <div className="flex flex-col gap-4 mt-4">
          <div className="card">
            <h3 className="text-sm font-bold text-arete-navy mb-4">{range}-Day Trend</h3>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={chartData} margin={{ top: 5, right: 5, bottom: 5, left: -20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#64748B' }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: '#64748B' }} />
                <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #E5E7EB', fontSize: 12 }} />
                <ReferenceLine y={75} stroke="#22C55E" strokeDasharray="4 2" strokeOpacity={0.4} />
                <Line dataKey="sleep" name="Sleep" stroke="#1E6FBF" strokeWidth={2} dot={false} />
                <Line dataKey="readiness" name="Readiness" stroke="#00A896" strokeWidth={2} dot={false} />
                <Line dataKey="hrv" name="HRV" stroke="#D4A843" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
            <div className="flex gap-4 mt-3 justify-center">
              {[
                { label: 'Sleep', color: '#1E6FBF' },
                { label: 'Readiness', color: '#00A896' },
                { label: 'HRV', color: '#D4A843' },
              ].map(({ label, color }) => (
                <div key={label} className="flex items-center gap-1.5 text-xs text-arete-muted">
                  <div className="w-3 h-0.5 rounded-full" style={{ backgroundColor: color }} />
                  {label}
                </div>
              ))}
            </div>
          </div>
          <BaselineDeviation metrics={metrics} />
          <HeatmapCalendar metrics={metrics} />
        </div>
      )}
    </div>
  );
});
